import { ChapterContextWarnings } from '@/components/project/JobContextWarnings'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Check, Columns2, Download, FileText, History, Library, Loader2, RotateCcw, RotateCw, WandSparkles, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { chapterApi, type RefineDonePayload } from '@/api/chapterApi'
import { characterApi } from '@/api/characterApi'
import { hasRefinementReferenceChange } from '@/lib/refinementReferences'
import { conflictApi } from '@/api/conflictApi'
import { creditsApi } from '@/api/creditsApi'
import { ApiError, friendlyMessage } from '@/api/http'
import { jobApi } from '@/api/jobApi'
import { readerApi, type ReaderExportFormat } from '@/api/readerApi'
import { getRuleLabel } from '@/api/ruleLabelsApi'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { ChapterGenerateForm } from '@/components/chapters/ChapterGenerateForm'
import { ChapterStreamView } from '@/components/chapters/ChapterStreamView'
import { ChapterQualityCard } from '@/components/quality/ChapterQualityCard'
import { QualityScoreBadge } from '@/components/quality/QualityScoreBadge'
import { buildChapterQualityRegenerateRequest, type QualityScore } from '@/components/quality/qualityGate'
import { isDocumentDarkTheme } from '@/lib/documentTheme'
import { downloadBlob } from '@/lib/download'
import { statusLabel } from '@/lib/statusLabel'
import { useUiStore } from '@/stores/uiStore'
import type { Chapter } from '@/types/nf'
import { useRuleLabels } from '@/hooks/useRuleLabels'
import { formatNumber } from '@/utils/format'
import { getChapterDisplayTitle } from '@/utils/chapterSelection'

interface ChapterWorkspaceProps {
  projectId: string
  branchId: string
  userId?: string
  chapters: Chapter[]
  chapter: Chapter | null
  quality?: QualityScore | null
  contentLanguage?: string
  onChapterUpdated?: () => Promise<unknown> | unknown
  initialDiff?: {
    activeContent: string
    candidate: CandidateVersionForDiff
  }
}

export interface CandidateVersionForDiff {
  id: number
  content: string
  status: string
  modelKey?: string
}

export type VersionDiffAction = 'return' | 'discard' | 'adopt' | 'restore'

export function getVersionDiffActions(status: string): VersionDiffAction[] {
  if (status === 'candidate') return ['return', 'discard', 'adopt']
  if (status === 'superseded') return ['return', 'restore']
  return ['return']
}

function ChapterMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background/40 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="nf-mono mt-1 text-sm text-foreground">{value}</p>
    </div>
  )
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isRefineDonePayload(value: unknown): value is RefineDonePayload {
  return isObject(value) && typeof value.version_id === 'number' && typeof value.content === 'string'
}

function errorMessageFromPayload(value: unknown, fallback: string) {
  if (isObject(value) && typeof value.error === 'string') return friendlyMessage(new Error(value.error), fallback)
  return friendlyMessage(new Error(String(value)), fallback)
}

function truncateConflictDetail(detail: string, maxLength = 60) {
  const text = detail.trim()
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}

export function ChapterWorkspace({ projectId, branchId, userId, chapters, chapter, quality, contentLanguage, onChapterUpdated, initialDiff }: ChapterWorkspaceProps) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const generatingChapter = useUiStore((s) => s.generatingChapter)
  const isGenerating = useUiStore((s) => s.isGenerating)
  const closeRefineRef = useRef<(() => void) | null>(null)
  const activeChapterIdRef = useRef<string | undefined>(chapter?.id)
  const [refining, setRefining] = useState(false)
  const [versionActionPending, setVersionActionPending] = useState(false)
  const [exportingFormat, setExportingFormat] = useState<ReaderExportFormat | null>(null)
  const [activeVersionContent, setActiveVersionContent] = useState(initialDiff?.activeContent ?? '')
  const [candidateVersion, setCandidateVersion] = useState<CandidateVersionForDiff | null>(initialDiff?.candidate ?? null)
  const referenceCharacters = useQuery({
    queryKey: ['refinement-characters', projectId, branchId],
    queryFn: () => characterApi.list(projectId),
    enabled: Boolean(candidateVersion && projectId),
  })
  const referenceWarning = candidateVersion?.status === 'candidate' && hasRefinementReferenceChange(
    activeVersionContent, candidateVersion.content, (Array.isArray(referenceCharacters.data) ? referenceCharacters.data : []).map((character) => character.name),
  )
  const [dismissedPartialChapterId, setDismissedPartialChapterId] = useState<string | null>(null)
  const scopedGeneratingChapter =
    generatingChapter && generatingChapter.projectId === projectId && generatingChapter.branchId === branchId
      ? generatingChapter
      : null

  const creditsQuery = useQuery({
    queryKey: ['credits', 'balance', userId],
    queryFn: () => creditsApi.balance(userId),
    enabled: Boolean(chapter && userId),
    staleTime: 30_000,
  })

  const qualityHistoryQuery = useQuery({
    queryKey: ['chapter-quality-history', chapter?.id, projectId],
    queryFn: () => chapterApi.qualityHistory(chapter?.id ?? '', projectId),
    enabled: Boolean(chapter?.id && projectId),
    staleTime: 15_000,
    refetchInterval: 3000,
  })

  const latestAssessmentId = qualityHistoryQuery.data?.at(-1)?.id
  useEffect(() => {
    if (!latestAssessmentId) return
    void queryClient.invalidateQueries({ queryKey: ['chapters'] })
    void queryClient.invalidateQueries({ queryKey: ['project-assets', projectId] })
    void queryClient.invalidateQueries({ queryKey: ['chapter-assets', chapter?.id] })
  }, [latestAssessmentId, projectId, chapter?.id, queryClient])

  const refreshChapterContent = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['chapters'] }),
      queryClient.invalidateQueries({ queryKey: ['chapter-versions', chapter?.id, projectId] }),
      queryClient.invalidateQueries({ queryKey: ['chapter-quality-history', chapter?.id, projectId] }),
      queryClient.invalidateQueries({ queryKey: ['projects'] }),
      queryClient.invalidateQueries({ queryKey: ['chapter-assets', chapter?.id] }),
      queryClient.invalidateQueries({ queryKey: ['project-assets', projectId] }),
      onChapterUpdated?.(),
    ])
  }

  const versionHistoryQuery = useQuery({
    queryKey: ['chapter-versions', chapter?.id, projectId],
    queryFn: () => chapterApi.listVersions(chapter?.id ?? '', projectId),
    enabled: Boolean(chapter?.id),
    staleTime: 10_000,
  })

  const runningJobsQuery = useQuery({
    queryKey: ['chapter-jobs', projectId, branchId, 'running'],
    queryFn: () => jobApi.list(projectId, { branchId, limit: 50 }),
    enabled: Boolean(projectId && branchId),
    refetchInterval: 3000,
  })

  const normalizedChapterStatus = chapter?.status?.trim().toLowerCase()
  const isNeedsReview = normalizedChapterStatus === 'needs_review'
  const showPartialResultWarning = normalizedChapterStatus === 'partial_result' && dismissedPartialChapterId !== chapter?.id
  const chapterConflictsQuery = useQuery({
    queryKey: ['memory-conflicts', projectId, chapter?.chapter_number, 'chapter-workspace'],
    queryFn: () => conflictApi.list(projectId, chapter?.chapter_number),
    enabled: Boolean(isNeedsReview && projectId && chapter?.chapter_number),
    staleTime: 15_000,
  })
  const ruleLabelsQuery = useRuleLabels({ enabled: isNeedsReview })

  const resubmitMutation = useMutation({
    mutationFn: async () => {
      if (!chapter) throw new Error(t('detail.pipelineViewer.selectChapter'))
      await chapterApi.resubmit(chapter.id)
    },
    onSuccess: async () => {
      toast.success(t('chapter.review.resubmitted'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['chapters'] }),
        queryClient.invalidateQueries({ queryKey: ['memory-conflicts', projectId] }),
        onChapterUpdated?.(),
      ])
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : friendlyMessage(error, t('common.serviceError')))
    },
  })

  useEffect(() => {
    activeChapterIdRef.current = chapter?.id
    closeRefineRef.current?.()
    closeRefineRef.current = null
    setRefining(false)
    setVersionActionPending(false)
    setActiveVersionContent('')
    setCandidateVersion(null)
    // 恢复未处置的精修候选 (gemini review B2): 切章/刷新后若存在 candidate 版本,
    // 自动拉回双栏对比, 防已扣费的精修内容在 UI 失联。ref 守卫防 await 期间切章串台。
    const chapterId = chapter?.id
    if (!chapterId) return
    void (async () => {
      try {
        const versions = await chapterApi.listVersions(chapterId, projectId)
        if (activeChapterIdRef.current !== chapterId) return
        const candidate = versions.find((version) => version.status === 'candidate')
        if (!candidate) return
        const active = versions.find((version) => version.status === 'active')
        const [activeDetail, candidateDetail] = await Promise.all([
          active ? chapterApi.getVersion(chapterId, active.id, projectId) : Promise.resolve(null),
          chapterApi.getVersion(chapterId, candidate.id, projectId),
        ])
        if (activeChapterIdRef.current !== chapterId) return
        setActiveVersionContent(activeDetail?.content ?? '')
        setCandidateVersion({
          id: candidate.id,
          content: candidateDetail?.content ?? '',
          status: candidate.status,
          modelKey: candidateDetail?.model_key,
        })
      } catch {
        // 恢复失败静默 (不阻塞正常阅读); 用户可重新精修
      }
    })()
  }, [chapter?.id, projectId])

  useEffect(() => {
    return () => {
      closeRefineRef.current?.()
      closeRefineRef.current = null
    }
  }, [])

  const qualityRegenerateMutation = useMutation({
    mutationFn: async () => {
      if (!chapter) throw new Error(t('detail.pipelineViewer.selectChapter'))
      const request = buildChapterQualityRegenerateRequest(chapter, contentLanguage)
      if (!request) throw new Error(t('qualityGate.emptyChapter'))
      await new Promise<void>((resolve, reject) => {
        let close: (() => void) | null = null
        const finish = () => {
          close?.()
          close = null
        }
        close = chapterApi.partialRegenerateStream(chapter.id, request, {
          onEvent: (event, data) => {
            if (event === 'done') {
              finish()
              resolve()
              return
            }
            if (event === 'error') {
              finish()
              reject(new Error(errorMessageFromPayload(data, t('detail.pipelineViewer.regenerateFailed'))))
            }
          },
          onError: (error) => {
            finish()
            reject(error)
          },
        })
      })
    },
    onSuccess: async () => {
      toast.success(t('qualityGate.regenerateQueued'))
      await onChapterUpdated?.()
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('detail.pipelineViewer.regenerateFailed')))
    },
  })

  const diffStyles = useMemo(
    () => ({
      variables: {
        light: {
          diffViewerBackground: 'transparent',
          gutterBackground: 'hsl(var(--muted))',
          codeFoldGutterBackground: 'hsl(var(--muted))',
        },
        dark: {
          diffViewerBackground: 'transparent',
          gutterBackground: 'hsl(var(--muted))',
          codeFoldGutterBackground: 'hsl(var(--muted))',
        },
      },
    }),
    [],
  )

  if (scopedGeneratingChapter) {
    return (
      <Card className="flex h-full min-h-[32rem] flex-col rounded-lg py-0">
        <CardHeader className="p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2 text-base">
                <Library className="size-4 text-primary" />
                {t('chapter.generate.stationTitle')}
              </CardTitle>
              <CardDescription className="mt-1">
                {isGenerating
                  ? t('chapter.generate.generatingChapter', { value: scopedGeneratingChapter.chapterNumber })
                  : t('chapter.generate.formHint')}
              </CardDescription>
            </div>
            <Badge variant="outline" className="w-fit rounded-md">
              {t('chapter.generate.stageBadge')}
            </Badge>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="min-h-0 flex-1 p-4">
          {isGenerating ? (
            <ChapterStreamView draft={scopedGeneratingChapter} userId={userId} />
          ) : (
            <ChapterGenerateForm
              projectId={projectId}
              branchId={branchId}
              chapters={chapters}
              draft={scopedGeneratingChapter}
              contentLanguage={contentLanguage}
            />
          )}
        </CardContent>
      </Card>
    )
  }

  if (!chapter) {
    return (
      <Card className="flex h-full min-h-[32rem] flex-col rounded-lg py-0">
        <CardHeader className="p-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Library className="size-4 text-primary" />
            {t('detail.contentWorkspace')}
          </CardTitle>
          <CardDescription>{t('detail.contentWorkspaceHint')}</CardDescription>
        </CardHeader>
        <Separator />
        <CardContent className="grid flex-1 place-items-center p-6">
          <div className="max-w-md rounded-lg border border-dashed bg-background/45 p-8 text-center">
            <FileText className="mx-auto size-7 text-muted-foreground" />
            <p className="mt-3 font-medium">{t('chapter.selectEmptyTitle')}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t('chapter.selectEmptyBody')}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const title = getChapterDisplayTitle(chapter, t('chapter.untitled'))
  const content = chapter.content?.trim()
  const status = chapter.status || 'unknown'
  const visual = statusLabel(status, 'chapter')
  const chapterMode = String(chapter.chapter_mode ?? '').toLowerCase()
  const isPremiumChapter = chapterMode === 'climax' || chapterMode === 'golden'
  const availableCredits = creditsQuery.data?.available ?? creditsQuery.data?.balance
  const creditsInsufficient = creditsQuery.data?.enabled === true && typeof availableCredits === 'number' && availableCredits <= 0
  const refineDisabled = refining || versionActionPending || !content || creditsInsufficient || creditsQuery.isLoading
  const useDarkTheme = isDocumentDarkTheme()
  const qualityHistory = qualityHistoryQuery.data ?? []
  const latestQuality = qualityHistory.at(-1) ?? null
  const versionHistory = versionHistoryQuery.data ?? []
  const runningJobs = (runningJobsQuery.data?.items ?? []).filter(
    (job) => !['done', 'failed', 'needs_review', 'partial_result'].includes(job.status),
  )
  const unresolvedConflicts = (chapterConflictsQuery.data ?? []).filter((row) => !row.overridden_at)
  const visibleConflicts = unresolvedConflicts.slice(0, 5)
  const hiddenConflictCount = Math.max(unresolvedConflicts.length - visibleConflicts.length, 0)
  const ruleLabels = ruleLabelsQuery.data?.rules
  const locale = i18n.resolvedLanguage ?? i18n.language
  const consistencyUrl = `/consistency?project=${encodeURIComponent(projectId)}&chapter=${encodeURIComponent(String(chapter.chapter_number))}`

  const loadRefineDiff = async (done: RefineDonePayload) => {
    // ref 守卫 (gemini review B3): await 期间用户切章则丢弃写入, 防旧章 diff 串到新章。
    const chapterId = chapter.id
    const versions = await chapterApi.listVersions(chapterId, projectId)
    if (activeChapterIdRef.current !== chapterId) return
    const active = versions.find((version) => version.status === 'active')
    const candidate = versions.find((version) => version.id === done.version_id)
    const [activeDetail, candidateDetail] = await Promise.all([
      active ? chapterApi.getVersion(chapterId, active.id, projectId) : Promise.resolve(null),
      candidate ? chapterApi.getVersion(chapterId, candidate.id, projectId) : Promise.resolve(null),
    ])
    if (activeChapterIdRef.current !== chapterId) return
    setActiveVersionContent(activeDetail?.content ?? content ?? '')
    setCandidateVersion({
      id: done.version_id,
      content: candidateDetail?.content ?? done.content,
      status: candidateDetail?.status ?? candidate?.status ?? done.status,
      modelKey: candidateDetail?.model_key ?? done.model_key,
    })
  }

  const finishRefine = () => {
    closeRefineRef.current?.()
    closeRefineRef.current = null
    setRefining(false)
  }

  const failRefine = (detail?: string) => {
    if (detail) console.warn('chapter refine failed', detail)
    toast.error(t('chapter.refine.fallbackKept'))
    finishRefine()
  }

  const handleRefine = () => {
    if (!chapter || !content || refineDisabled) return
    closeRefineRef.current?.()
    setRefining(true)
    setCandidateVersion(null)
    setActiveVersionContent(content)
    closeRefineRef.current = chapterApi.refineStream(chapter.id, projectId, contentLanguage, {
      onEvent: (event, data) => {
        if (event === 'done') {
          if (!isRefineDonePayload(data)) {
            failRefine('invalid done payload')
            return
          }
          void loadRefineDiff(data)
            .then(() => {
              toast.success(t('chapter.refine.generated'))
              finishRefine()
              void creditsQuery.refetch()
              void queryClient.invalidateQueries({ queryKey: ['chapter-versions', chapter.id, projectId] })
              void queryClient.invalidateQueries({ queryKey: ['projects'] })
            })
            .catch((err) => {
              failRefine(friendlyMessage(err, t('chapter.refine.fallbackKept')))
            })
          return
        }
        if (event === 'error') {
          failRefine(errorMessageFromPayload(data, t('chapter.refine.fallbackKept')))
        }
      },
      onError: (err) => failRefine(friendlyMessage(err, t('chapter.refine.fallbackKept'))),
    })
  }

  const handleAdopt = async () => {
    if (!candidateVersion) return
    setVersionActionPending(true)
    try {
      await chapterApi.adoptVersion(chapter.id, candidateVersion.id, projectId)
      toast.success(t('chapter.version.adoptSuccess'))
      setCandidateVersion(null)
      await refreshChapterContent()
      void creditsQuery.refetch()
    } catch (err) {
      toast.error(friendlyMessage(err, t('common.serviceError')))
    } finally {
      setVersionActionPending(false)
    }
  }

  const handleReturnFromDiff = () => {
    setCandidateVersion(null)
  }

  const handleDiscard = async () => {
    if (!candidateVersion) return
    setVersionActionPending(true)
    try {
      await chapterApi.discardVersion(chapter.id, candidateVersion.id, projectId)
      toast.success(t('chapter.version.keepDraftSuccess'))
      setCandidateVersion(null)
      await refreshChapterContent()
    } catch (err) {
      toast.error(friendlyMessage(err, t('common.serviceError')))
    } finally {
      setVersionActionPending(false)
    }
  }

  const handleRestore = async () => {
    if (!candidateVersion) return
    setVersionActionPending(true)
    try {
      await chapterApi.restoreVersion(chapter.id, candidateVersion.id, projectId)
      toast.success(t('chapter.version.restoreSuccess'))
      setCandidateVersion(null)
      await refreshChapterContent()
    } catch (err) {
      toast.error(friendlyMessage(err, t('common.serviceError')))
    } finally {
      setVersionActionPending(false)
    }
  }

  const handleExport = async (format: ReaderExportFormat, scope: 'chapter' | 'volume') => {
    if (!chapter || exportingFormat) return
    setExportingFormat(format)
    try {
      const file =
        scope === 'chapter'
          ? await readerApi.exportChapter(projectId, chapter.chapter_number, format, branchId)
          : await readerApi.exportVolume(projectId, Number(chapter.volume_no ?? 1), format, branchId)
      downloadBlob(file.blob, file.filename)
      toast.success(scope === 'chapter' ? '章节已导出' : '卷已导出')
    } catch (err) {
      toast.error(friendlyMessage(err, '导出失败'))
    } finally {
      setExportingFormat(null)
    }
  }

  const handleCompareVersion = async (versionId: number) => {
    if (!chapter) return
    setVersionActionPending(true)
    try {
      const active = versionHistory.find((version) => version.status === 'active')
      const [activeDetail, selectedDetail] = await Promise.all([
        active ? chapterApi.getVersion(chapter.id, active.id, projectId) : Promise.resolve(null),
        chapterApi.getVersion(chapter.id, versionId, projectId),
      ])
      setActiveVersionContent(activeDetail?.content ?? content ?? '')
      setCandidateVersion({
        id: selectedDetail.id,
        content: selectedDetail.content,
        status: selectedDetail.status,
        modelKey: `版本 ${selectedDetail.version_no}`,
      })
    } catch (err) {
      toast.error(friendlyMessage(err, '版本读取失败'))
    } finally {
      setVersionActionPending(false)
    }
  }

  return (
    <Card className="flex h-full min-h-[32rem] flex-col rounded-lg py-0">
      <CardHeader className="p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <CardTitle className="truncate text-base">{title}</CardTitle>
            <CardDescription className="mt-1">
              {t('chapter.chapterNumber', { value: chapter.chapter_number })}
            </CardDescription>
          </div>
          <Badge variant="outline" className="w-fit rounded-md">
            {t(visual.textKey, visual.textOptions)}
          </Badge>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <QualityScoreBadge quality={quality} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => qualityRegenerateMutation.mutate()}
            disabled={!content || qualityRegenerateMutation.isPending}
            title={!content ? t('qualityGate.emptyChapter') : t('qualityGate.regenerate')}
          >
            {qualityRegenerateMutation.isPending ? <Loader2 className="animate-spin" /> : <RotateCw />}
            {t('qualityGate.regenerate')}
          </Button>
          <Button
            type="button"
            variant={isPremiumChapter ? 'default' : 'outline'}
            size="sm"
            onClick={handleRefine}
            disabled={refineDisabled}
            title={creditsInsufficient ? '余额不足' : undefined}
          >
            {refining ? <Loader2 className="animate-spin" /> : <WandSparkles />}
            {t('chapter.refine.action')}
          </Button>
          {creditsInsufficient ? <span className="text-xs text-muted-foreground">余额不足</span> : null}
          {candidateVersion ? (
            <Badge variant="secondary" className="rounded-md">
              <Columns2 className="size-3" />
              {t('refinement.candidate')}
            </Badge>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleExport('txt', 'chapter')}
            disabled={Boolean(exportingFormat)}
          >
            {exportingFormat === 'txt' ? <Loader2 className="animate-spin" /> : <Download />}
            TXT
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleExport('md', 'chapter')}
            disabled={Boolean(exportingFormat)}
          >
            {exportingFormat === 'md' ? <Loader2 className="animate-spin" /> : <Download />}
            Markdown
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleExport('txt', 'volume')}
            disabled={Boolean(exportingFormat)}
            title={t('refinement.exportVolumeTXT')}
          >
            <Download />
            {t('refinement.volumeTXT')}
          </Button>
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <ChapterMeta label={t('chapter.words')} value={formatNumber(chapter.word_count ?? 0)} />
          <ChapterMeta label={t('chapter.status')} value={t(visual.textKey, visual.textOptions)} />
          <ChapterMeta label={t('chapter.branch')} value={chapter.branch_id} />
        </div>
        <div className="mb-4 rounded-md border border-dashed bg-background/40 p-3 text-xs text-muted-foreground">
          {t('chapter.s2cHint')}
        </div>
        {isNeedsReview ? (
          <Alert variant="warning" className="mb-4">
            <AlertTriangle className="size-4" />
            <AlertTitle>{t('chapter.review.title')}</AlertTitle>
            <AlertDescription>
              <p>{t('chapter.review.description')}</p>
              {unresolvedConflicts.length ? (
                <div className="mt-2 space-y-1 text-xs">
                  <p>{t('chapter.review.conflictListTitle', { count: unresolvedConflicts.length })}</p>
                  <ol className="space-y-1">
                    {visibleConflicts.map((row, index) => {
                      const severityIcon = row.severity === 'hard' ? '🔴' : '🟡'
                      const severityLabel = t(`chapter.review.severity.${row.severity}`)
                      const ruleLabel = getRuleLabel(ruleLabels, row.rule_id, locale)
                      const detail = truncateConflictDetail(row.detail || row.rule_id)
                      return (
                        <li key={row.id} className="leading-relaxed">
                          <span className="nf-mono">{index + 1}.</span>{' '}
                          <span aria-label={severityLabel} title={severityLabel}>
                            {severityIcon}
                          </span>{' '}
                          <span className="font-medium text-foreground">{ruleLabel}</span>：{detail}
                        </li>
                      )
                    })}
                  </ol>
                  {hiddenConflictCount ? (
                    <p className="text-muted-foreground">{t('chapter.review.moreConflicts', { count: hiddenConflictCount })}</p>
                  ) : null}
                </div>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to={consistencyUrl}>{t('chapter.review.viewConflicts')}</Link>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => resubmitMutation.mutate()}
                  disabled={resubmitMutation.isPending}
                >
                  <RotateCcw className={resubmitMutation.isPending ? 'animate-spin' : undefined} />
                  {t('chapter.review.resubmit')}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : null}
        {showPartialResultWarning ? (
          <Alert variant="warning" className="mb-4">
            <AlertTriangle className="size-4" />
            <AlertTitle>{t('chapter.partialResult.title')}</AlertTitle>
            <AlertDescription>
              <p>{t('chapter.partialResult.description')}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => resubmitMutation.mutate()}
                  disabled={resubmitMutation.isPending}
                >
                  <RotateCcw className={resubmitMutation.isPending ? 'animate-spin' : undefined} />
                  {t('chapter.partialResult.regenerate')}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setDismissedPartialChapterId(chapter.id)}>
                  <Check />
                  {t('chapter.partialResult.keep')}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="mb-4">
          <ChapterContextWarnings chapterId={chapter.id} />
          <ChapterQualityCard quality={latestQuality} history={qualityHistory} loading={qualityHistoryQuery.isLoading} />
        </div>
        {runningJobs.length ? (
          <div className="mb-4 rounded-md border bg-background/45 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Loader2 className="size-4 animate-spin text-primary" />
              连续生成进度
            </div>
            <div className="space-y-2">
              {runningJobs.map((job) => {
                const total = Math.max(job.total_segments || 0, 1)
                const percent = Math.min(100, Math.round(((job.current_seg || 0) / total) * 100))
                return (
                  <div key={job.id} className="grid gap-1 text-xs">
                    <div className="flex items-center justify-between gap-3">
                      <span>
                        {t('board.chapter', { number: formatNumber(job.chapter_no) })} ·{' '}
                        {t(statusLabel(job.status, 'job').textKey)}
                      </span>
                      <span className="nf-mono">{percent}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-primary transition-all" style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">SSE 断开或刷新后会继续按 job 状态轮询恢复进度。</p>
          </div>
        ) : null}
        {versionHistory.length ? (
          <div className="mb-4 rounded-md border bg-background/45 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <History className="size-4 text-primary" />
              {t('refinement.history')}
            </div>
            <div className="flex flex-wrap gap-2">
              {versionHistory.map((version) => (
                <Button
                  key={version.id}
                  type="button"
                  variant={version.status === 'active' ? 'secondary' : 'outline'}
                  size="sm"
                  disabled={versionActionPending}
                  onClick={() => void handleCompareVersion(version.id)}
                >
                  v{version.version_no} · {version.status}
                </Button>
              ))}
            </div>
          </div>
        ) : null}
        {candidateVersion ? (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">{t('chapter.version.diffDirection')}</p>
            {referenceWarning ? <Alert role="alert"><AlertTriangle /><AlertDescription>{t('refinement.referenceWarning')}</AlertDescription></Alert> : null}
            <div className="overflow-auto rounded-md border">
              <ReactDiffViewer
                oldValue={activeVersionContent}
                newValue={candidateVersion.content}
                splitView
                compareMethod={DiffMethod.WORDS}
                leftTitle={t('chapter.version.currentVersion')}
                rightTitle={t('chapter.version.selectedVersion')}
                useDarkTheme={useDarkTheme}
                hideSummary
                disableWorker
                styles={diffStyles}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button variant="ghost" onClick={handleReturnFromDiff} disabled={versionActionPending}>
                <ArrowLeft />
                {t('chapter.version.back')}
              </Button>
              <div className="flex flex-wrap justify-end gap-2">
                {getVersionDiffActions(candidateVersion.status).includes('discard') ? (
                  <Button variant="outline" onClick={() => void handleDiscard()} disabled={versionActionPending}>
                    <X />
                    {t('chapter.version.keepDraft')}
                  </Button>
                ) : null}
                {getVersionDiffActions(candidateVersion.status).includes('restore') ? (
                  <Button variant="outline" onClick={() => void handleRestore()} disabled={versionActionPending}>
                    <RotateCcw className={versionActionPending ? 'animate-spin' : undefined} />
                    {t('chapter.version.restore')}
                  </Button>
                ) : null}
                {getVersionDiffActions(candidateVersion.status).includes('adopt') ? (
                  <Button onClick={() => void handleAdopt()} disabled={versionActionPending}>
                    <Check />
                    {t('chapter.version.adopt')}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : content ? (
          <article className="space-y-4 text-sm leading-7 text-foreground">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={{
                h1: ({ children }) => <h1 className="text-xl font-semibold tracking-normal">{children}</h1>,
                h2: ({ children }) => <h2 className="text-lg font-semibold tracking-normal">{children}</h2>,
                h3: ({ children }) => <h3 className="text-base font-semibold tracking-normal">{children}</h3>,
                p: ({ children }) => <p>{children}</p>,
                ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-2 border-border pl-4 text-muted-foreground">{children}</blockquote>
                ),
                code: ({ children }) => <code className="rounded bg-muted px-1 py-0.5 nf-mono text-xs">{children}</code>,
                pre: ({ children }) => <pre className="overflow-auto rounded-md bg-muted p-3">{children}</pre>,
              }}
            >
              {content}
            </ReactMarkdown>
          </article>
        ) : (
          <div className="rounded-md border border-dashed bg-background/45 p-8 text-center text-sm text-muted-foreground">
            {t('chapter.contentEmpty')}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
